/* @flow */

import React from 'react';
import moment from 'moment';

import Alert from '@material-ui/lab/Alert';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import AmountFormatterComponent from 'components/formatters/amount';
import ButtonComponent from 'components/buttons/button';
import FormErrorsComponent from 'components/formErrors';
import IntlMessageComponent from 'components/formatters/intlMessage';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';

type SellInvoiceBatchTableViewComponentProps = {
  entries: any[];
  sum: number;
  minDebtAmount: number;
  isProcessing: boolean;
  formErrors: string[];
  addInvoice: () => void;
  editEntry: (index: number) => void;
  removeEntry: (index: number) => void;
  cancelInvoice: () => void;
  submitBatch: () => void;
}

export default function SellInvoiceBatchTableViewComponent(props: SellInvoiceBatchTableViewComponentProps) {

  const canSubmit = props.entries.length > 0 && props.sum >= props.minDebtAmount;

  return (
    <React.Fragment>
      <FormErrorsComponent formErrors={props.formErrors} />

      <PageSubTitleViewComponent id="page.sellInvoice.tab2.title" />

      <Box mt={2}>
        <IntlMessageComponent id="page.sellInvoice.tab2.batch.intro" values={{minDebtAmount: props.minDebtAmount}} />
      </Box>

      <Box mt={2} mb={2}>
        <Alert severity="info">
          <IntlMessageComponent id="page.sellInvoice.tab2.batch.contactInfo" />
        </Alert>
      </Box>

      {props.entries.length > 0 &&
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><IntlMessageComponent id="page.sellInvoice.tab2.batch.table.reference" /></TableCell>
              <TableCell><IntlMessageComponent id="page.sellInvoice.tab2.batch.table.clientReference" /></TableCell>
              <TableCell><IntlMessageComponent id="page.sellInvoice.tab2.batch.table.issueDate" /></TableCell>
              <TableCell><IntlMessageComponent id="page.sellInvoice.tab2.batch.table.dueDate" /></TableCell>
              <TableCell align="right"><IntlMessageComponent id="page.sellInvoice.tab2.batch.table.invoiceTotal" /></TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {props.entries.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>{entry.reference}</TableCell>
                <TableCell>{entry.debtorReference}</TableCell>
                <TableCell>{entry.issueDate ? moment(entry.issueDate).format('DD/MM/YYYY') : ''}</TableCell>
                <TableCell>{entry.dueDate ? moment(entry.dueDate).format('DD/MM/YYYY') : ''}</TableCell>
                <TableCell align="right">
                  <AmountFormatterComponent amount={entry.debtAmount} currency="EUR" minimumFractionDigits={0} maximumFractionDigits={2} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" disabled={props.isProcessing} onClick={() => props.editEntry(index)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" disabled={props.isProcessing} onClick={() => props.removeEntry(index)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={4} />
              <TableCell align="right">
                <strong><AmountFormatterComponent amount={props.sum} currency="EUR" minimumFractionDigits={0} maximumFractionDigits={2} /></strong>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      }

      <Box mt={5}>
        <ButtonComponent
          color="default"
          variant="contained"
          disabled={props.isProcessing}
          onClick={props.cancelInvoice}
        ><IntlMessageComponent id="page.sellInvoice.button.cancelInvoice" /></ButtonComponent>

        <Box component="span" ml={1}>
          <ButtonComponent
            color="secondary"
            variant="contained"
            disabled={props.isProcessing}
            onClick={props.addInvoice}
          ><IntlMessageComponent id="page.sellInvoice.tab2.batch.button.addInvoice" /></ButtonComponent>
        </Box>

        {canSubmit &&
          <Box component="span" ml={1}>
            <ButtonComponent
              color="primary"
              variant="contained"
              isProcessing={props.isProcessing}
              onClick={props.submitBatch}
            ><IntlMessageComponent id="page.sellInvoice.tab2.batch.button.submitInvoice" /></ButtonComponent>
          </Box>
        }
      </Box>
    </React.Fragment>
  );
}
